import * as React from 'react';
import { ModalData, ModalType } from '../data/ModalData';
import { SoundtrackSelectionOverlay } from '../onboarding/SoundtrackTypeSelectionOverlay';
import LoopSelection from '../audio-editing/LoopSelection';
import Label from '../audio-editing/Label';
import LabelDetails from '../audio-editing/LabelDetails';
import ITunesSearch from '../audio-editing/ITunesSearch';
import FeedbackCancel from '../feedback/FeedbackCancel';
import AddResolutionDialog from '../showcase/AddResolutionDialog';
import Layer from '../data/Layer';
import SplitLayerOverlay from '../split-layer';
import { AudioEditing } from '../audio-editing/AudioEditing';
import { canUseWebGL } from '../utils';
import { NoWebGL } from '../not-supported/NoWebGL';
import { PartiallySupported } from '../not-supported/PartiallySupported';
import { WelcomeToBeatFlyerLite } from '../showcase/WelcomeToBeatFlyerLite';
import { Export } from '../export/Export';
import { UnsplashPhotoSelector } from '../modals/UnsplashPhotoSelector';
import { PatternSelector } from '../modals/PatternSelector';
import Recorder from '../video/Recorder';
import { RecordingModal } from '../video/RecordingModal';
import { ProcessingModal } from './ProcessingModal';
import { RecordingReviewModal } from '../video/RecordingReviewModal';
import RecordType from '../data/RecordType';
import { RecordingShareModal } from '../video/RecordingShareModal';
import { SoundWaveSelector } from '../modals/SoundWaveSelector';
import { GradientSelector } from '../modals/GradientSelector';
import { StickerSelector } from '../modals/StickerSelector';
import { TemplateUI } from 'data/FlyerData';
import { TemplateImageReplaceModal } from 'modals/TemplateImageReplaceModal';
import { GenreSelection } from 'audio-editing/GenreSelection';
import MusicGenre from 'audio-editing/MusicGenre';
import { CaptionsModal } from 'captions/CaptionsModal';
import { AddColorThemeModal } from 'account/AddColorThemeModal';
import { ShapeSelector } from 'modals/ShapeSelector';
import { UploadCustomFontModal } from 'account/UploadCustomFontModal';
import { PlaybackSmoothness } from 'data/PlaybackSmoothness';
import { BuyFigmaSubscriptionModal } from 'modals/BuyFigmaSubscription';
import { SegmentationSurvey } from 'modals/SegmentationSurvey';
import { FromDataOnboarding } from 'modals/FromDataOnboarding';

interface Props {
  modals: ModalData[];
}

export const ModalManager: React.FunctionComponent<Props> = props => {
  const { modals } = props;
  return (
    <>
      {modals.map((item, index) => {
        switch (item.type) {
          case ModalType.PickMusicSource:
            return <SoundtrackSelectionOverlay key={index} />;

          case ModalType.ChooseGenre:
            return <GenreSelection key={index} />;

          case ModalType.ChooseLoop:
            const genre = item.data as MusicGenre;
            return (
              <LoopSelection
                // onSelectLabel={this.didSelectLabel}
                // blur={this.state.selectedLabel !== null}
                genre={genre}
                key={index}
              />
            );

          case ModalType.LabelDetails:
            const label = item.data as Label;
            return <LabelDetails data={label} key={index} />;

          case ModalType.ITunesSearch:
            return <ITunesSearch key={index} />;

          case ModalType.DeleteAccount:
            return <FeedbackCancel key={index} />;

          case ModalType.AddResolution:
            return <AddResolutionDialog key={index} />;

          case ModalType.SplitLayer: {
            const layer = item.data as Layer;
            return (
              <SplitLayerOverlay
                layer={layer}
                skipQuestion={true}
                key={index}
              />
            );
          }

          case ModalType.EditAudio:
            return (
              <AudioEditing
                audioFile={item.data.file}
                thumbUrl={item.data.thumbUrl}
                title={item.data.title}
                artist={item.data.artist}
                key={index}
              />
            );

          case ModalType.UnsupportedBrowser:
            return canUseWebGL() ? (
              <PartiallySupported key={index} />
            ) : (
              <NoWebGL key={index} />
            );

          case ModalType.FigmaWelcome:
            return <WelcomeToBeatFlyerLite key={index} />;

          case ModalType.Export:
            return <Export key={index} />;

          case ModalType.Unsplash: {
            const layerOrProperty: Layer | TemplateUI | undefined = item.data;
            return (
              <UnsplashPhotoSelector
                layerOrProperty={layerOrProperty}
                key={index}
              />
            );
          }

          case ModalType.Gradients: {
            const layer: Layer | undefined = item.data;
            return <GradientSelector layer={layer} key={index} />;
          }

          case ModalType.Patterns: {
            const layer: Layer | undefined = item.data;
            return <PatternSelector layer={layer} key={index} />;
          }

          case ModalType.Shapes: {
            const layer: Layer | undefined = item.data.layer;
            const isMask = layer !== undefined && item.data.isMask === true;
            return <ShapeSelector layer={layer} isMask={isMask} key={index} />;
          }

          case ModalType.SoundWaves: {
            const layer: Layer | undefined = item.data;
            return <SoundWaveSelector layer={layer} key={index} />;
          }

          case ModalType.Stickers: {
            const layer: Layer | undefined = item.data;
            return <StickerSelector layer={layer} key={index} />;
          }

          case ModalType.Recording: {
            const recorder: Recorder = item.data;
            return <RecordingModal recorder={recorder} key={index} />;
          }

          case ModalType.Processing: {
            const action: string = item.data;
            return <ProcessingModal action={action} key={index} />;
          }

          case ModalType.ReviewRecording: {
            const recordType: RecordType = item.data.type;
            const recordingUrl: string = item.data.url;
            const recordingSmoothness = item.data
              .smoothness as PlaybackSmoothness;
            return (
              <RecordingReviewModal
                recordType={recordType}
                recordingUrl={recordingUrl}
                recordingSmoothness={recordingSmoothness}
                key={index}
              />
            );
          }

          case ModalType.ShareVideo: {
            const recordingUrl: string = item.data;
            return (
              <RecordingShareModal recordingUrl={recordingUrl} key={index} />
            );
          }

          case ModalType.TemplateImageReplacement: {
            const property: TemplateUI = item.data;
            return (
              <TemplateImageReplaceModal property={property} key={index} />
            );
          }

          case ModalType.CaptionsEditing: {
            return <CaptionsModal {...item.data} key={index} />;
          }

          case ModalType.AddColorTheme: {
            return <AddColorThemeModal key={index} />;
          }

          case ModalType.UploadCustomFont: {
            const file: File = item.data.file;
            const fontName: string = item.data.fontName;
            return (
              <UploadCustomFontModal
                file={file}
                initialName={fontName}
                key={index}
              />
            );
          }

          case ModalType.BuyFigmaSubscription:
            return <BuyFigmaSubscriptionModal key={index} />;

          case ModalType.SegmentationSurvey: {
            const animated = item.data !== false;
            return <SegmentationSurvey animated={animated} key={index} />;
          }

          case ModalType.FromDataOnboarding:
            return <FromDataOnboarding key={index} />;

          default:
            return null;
        }
      })}
    </>
  );
};



// WEBPACK FOOTER //
// ./src/components/ModalManager.tsx